import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card } from "@/components/ui/card";
import { ArrowRight, LogOut, Star } from "lucide-react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { logout } from "@/services/authService";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { fetchUserProfile } from "@/utils/apiUtils";
import { useQuery } from "@tanstack/react-query";

const Account = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { logout: contextLogout } = useAuth();
  
  // Fetch user profile data
  const { data: profileData, isLoading, error } = useQuery({
    queryKey: ["userProfile"],
    queryFn: fetchUserProfile,
    staleTime: 300000, // 5 minutes
    retry: 2,
  });

  const userInitial = profileData?.first_name 
    ? profileData.first_name.charAt(0).toUpperCase()
    : profileData?.username
      ? profileData.username.charAt(0).toUpperCase()
      : "D";

  // Calculate values to display
  const rating = profileData?.avg_rating || 0;
  const reviewCount = profileData?.num_ratings || 0;
  const rideCount = profileData?.num_trips || 0;
  const earnings = profileData?.lifetime_payouts_usd || 0;
  const fullName = profileData?.full_name || profileData?.username || "User";
  const companyName = profileData?.organization?.name || "No Company Registered";

  const handleLogout = async () => {
    try {
      await logout();
      contextLogout(); // Update auth context state
      toast({
        title: "Logged out successfully",
        description: "You have been logged out of your account.",
      });
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      toast({
        title: "Logout failed",
        description: "An error occurred while logging out. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Handle loading state
  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="container mx-auto px-4 py-12 flex justify-center items-center h-64">
          <p className="text-lg text-gray-600">Loading account information...</p>
        </div>
      </DashboardLayout>
    );
  }

  // Handle error state
  if (error) {
    return (
      <DashboardLayout>
        <div className="container mx-auto px-4 py-12">
          <Card className="p-8 shadow-md bg-white border border-gray-200">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-black mb-4">Error Loading Account</h1>
              <p className="text-gray-600 mb-6">
                We couldn't load your account information. Please try refreshing the page.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={() => window.location.reload()}>
                  Refresh Page
                </Button>
                <Button 
                  onClick={handleLogout}
                  variant="outline" 
                  className="flex items-center justify-center gap-2 border-red-500 text-red-500 hover:bg-red-50 hover:text-red-600"
                >
                  <LogOut size={18} />
                  <span>Log Out</span>
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto px-4 pb-12 bg-white">
        <div className="max-w-2xl mx-auto">
          <Card className="p-8 shadow-md bg-white border border-gray-200">
            <div className="flex flex-col items-center mb-6">
              <Avatar className="h-20 w-20 mb-4">
                <AvatarImage src={profileData?.avatar_url || ""} alt={fullName} />
                <AvatarFallback className="text-2xl bg-neon-green text-black">
                  {userInitial}
                </AvatarFallback>
              </Avatar>
              <h1 className="text-3xl font-bold mb-1 text-black">{fullName}</h1>
              <p className="text-gray-500">My Earnings</p>
              <p className="text-4xl font-bold mt-2 text-black">${earnings.toLocaleString()}</p>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-sm text-gray-600 mb-2">Company</h3>
                <div className="bg-gray-100 p-3 rounded-md text-black">
                  {companyName}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm text-gray-600 mb-2">Rating</h3>
                  <div className="bg-gray-100 p-3 rounded-md flex items-center">
                    <span className="text-4xl font-bold mr-2 text-black">{rating.toFixed(1)}</span>
                    <div className="flex flex-col">
                      <div className="flex text-neon-green">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            className={i < Math.floor(rating) ? "fill-neon-green" : "text-gray-300"} 
                            size={16} 
                          />
                        ))}
                      </div>
                      <span className="text-gray-500 text-sm">{reviewCount} reviews</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm text-gray-600 mb-2"># of Rides</h3>
                  <div className="bg-gray-100 p-3 rounded-md">
                    <span className="text-4xl font-bold text-black">{rideCount}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3 pt-4">
                <button 
                  onClick={() => toast({
                    title: "Coming Soon",
                    description: "Please reach out to team@discoveryconciergeservices.com for any changes needed",
                  })}
                  className="w-full"
                >
                  <div className="bg-gray-200 text-black rounded-md p-4 flex justify-between items-center transition-colors duration-300 hover:bg-gray-300">
                    <span>Manage My Account</span>
                    <ArrowRight size={18} />
                  </div>
                </button>
                
                <button 
                  onClick={() => toast({
                    title: "Coming Soon",
                    description: "Please reach out to team@discoveryconciergeservices.com for any changes needed",
                  })}
                  className="w-full"
                >
                  <div className="bg-gray-200 text-black rounded-md p-4 flex justify-between items-center transition-colors duration-300 hover:bg-gray-300">
                    <span>Manage My Fleet</span>
                    <ArrowRight size={18} />
                  </div>
                </button>
                
                <button 
                  onClick={() => toast({
                    title: "Coming Soon",
                    description: "Please reach out to team@discoveryconciergeservices.com for any changes needed",
                  })}
                  className="w-full"
                >
                  <div className="bg-gray-200 text-black rounded-md p-4 flex justify-between items-center transition-colors duration-300 hover:bg-gray-300">
                    <span>Manage My Payout Portal</span>
                    <ArrowRight size={18} />
                  </div>
                </button>
                
                <button 
                  onClick={() => toast({
                    title: "Coming Soon",
                    description: "Please reach out to team@discoveryconciergeservices.com for any changes needed",
                  })}
                  className="w-full"
                >
                  <div className="bg-gray-200 text-black rounded-md p-4 flex justify-between items-center transition-colors duration-300 hover:bg-gray-300">
                    <span>View My T&S Agreements</span>
                    <ArrowRight size={18} />
                  </div>
                </button>
              </div>
              
              {/* Logout Button */}
              <div className="pt-6">
                <Button 
                  onClick={handleLogout}
                  variant="outline" 
                  className="w-full flex items-center justify-center gap-2 border-red-500 text-red-500 hover:bg-red-50 hover:text-red-600"
                >
                  <LogOut size={18} />
                  <span>Log Out</span>
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Account;
